import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import { useAuth } from '../../../context/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import apiClient from '../../../lib/api';

const AddCardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDesigner = user?.type === 'designer';
  const isAdmin = user?.type === 'admin';
  
  const [formData, setFormData] = useState({
    designerId: isDesigner ? (user?.id || '') : '',
    title: '',
    tags: [],
  });
  const [designers, setDesigners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDesigners, setLoadingDesigners] = useState(true);
  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    // Only load designers if the user is an admin
    if (isAdmin) {
      const loadDesigners = async () => {
        try {
          setLoadingDesigners(true);
          const response = await apiClient.getDesigners({ limit: 100 });
          if (response.data) {
            setDesigners(response.data);
          }
        } catch (error) {
          console.error('Error loading designers:', error);
          toast.error('Failed to load designers');
        } finally {
          setLoadingDesigners(false);
        }
      };
      loadDesigners();
    } else {
      setLoadingDesigners(false);
    }
  }, [isAdmin]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      handleInputChange('tags', [...formData.tags, tagInput.trim()]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    handleInputChange('tags', formData.tags.filter(tag => tag !== tagToRemove));
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleCancel = () => {
    navigate('/cards');
  };

  const validateForm = () => {
    const newErrors = {};

    // Only validate designerId for admins (designers have it auto-set)
    if (isAdmin && !formData.designerId.trim()) {
      newErrors.designerId = 'Designer is required';
    }
    // Ensure designerId is set for designers
    if (isDesigner && !formData.designerId) {
      newErrors.designerId = 'Designer ID is missing';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    try {
      setIsLoading(true);
      // Ensure tags are always included, even if empty array, and status is always draft
      const cardData = {
        ...formData,
        status: 'draft',
        tags: formData.tags || [],
      };
      console.log('Creating card with data:', cardData);
      const response = await apiClient.createCard(cardData);
      
      if (response.success && response.data) {
        toast.success('Card created successfully');
        navigate(`/cards/${response.data.id}`);
      } else {
        toast.error(response.error || 'Failed to create card');
      }
    } catch (error) {
      console.error('Error creating card:', error);
      toast.error(error.response?.data?.error || 'Failed to create card');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DashboardLayout title="Add New Card">
      <Box sx={{ maxWidth: 800, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1">
            Add New Card
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
            Complete the form below to add a new card to the system
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: 3 
              }}>
                {isAdmin && (
                  <FormControl fullWidth error={!!errors.designerId}>
                    <InputLabel>Designer *</InputLabel>
                    <Select
                      value={formData.designerId}
                      onChange={(e) => handleInputChange('designerId', e.target.value)}
                      label="Designer *"
                      disabled={loadingDesigners}
                    >
                      {loadingDesigners ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} />
                          Loading designers...
                        </MenuItem>
                      ) : designers.length === 0 ? (
                        <MenuItem disabled>No designers available</MenuItem>
                      ) : (
                        designers.map((designer) => (
                          <MenuItem key={designer.id} value={designer.id}>
                            {designer.firstName} {designer.lastName} ({designer.email})
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {errors.designerId && (
                      <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                        {errors.designerId}
                      </Typography>
                    )}
                  </FormControl>
                )}

                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  error={!!errors.title}
                  helperText={errors.title}
                />

                <Box>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    {formData.tags.map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        onDelete={() => handleRemoveTag(tag)}
                        size="small"
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <TextField
                      size="small"
                      placeholder="Add a tag"
                      value={tagInput}
                      onChange={(e) => setTagInput(e.target.value)}
                      onKeyPress={handleTagInputKeyPress}
                      sx={{ flex: 1 }}
                    />
                    <Button
                      type="button"
                      variant="outlined"
                      onClick={handleAddTag}
                      disabled={!tagInput.trim() || formData.tags.includes(tagInput.trim())}
                    >
                      Add
                    </Button>
                  </Box>
                </Box>
              </Box>

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end',
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Button
                  onClick={handleCancel}
                  startIcon={<ArrowBackIcon />}
                  disabled={isLoading}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isLoading}
                  startIcon={<CheckIcon />}
                >
                  {isLoading ? 'Creating...' : 'Create Card'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
};

export default AddCardPage;

